import { HttpClient } from '@angular/common/http';
import { Injectable, NgZone, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, Observable, filter, lastValueFrom } from 'rxjs';
import { JwtHelperService } from '@auth0/angular-jwt';
import { environment } from '../../../environments/environment';
import { UserData } from '../../login/userdata.model';
import { MidaApiService } from '../../mida-api.service';
import { CoreService } from './core.service';

import { ConfigService } from '../../core/services/config.service';
import { NavigationHistoryService } from '../services/navigation-history.service';
@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private config = inject(ConfigService);

  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly midaApiService = inject(MidaApiService);
  private readonly coreService = inject(CoreService);
  private readonly navigationHistory = inject(NavigationHistoryService);
  private readonly ngZone = inject(NgZone);

  private jwtHelper: JwtHelperService = new JwtHelperService();
  private apiUrl = environment;

  private refreshInProgress$ = new BehaviorSubject<Promise<string> | null>(null);
  private timeoutId: ReturnType<typeof setTimeout> | null = null;
  private sessionTimeoutValue: number = 5;

  constructor() {
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(() => {
        if (this.isExcludedFromSessionTimeout()) {
          this.clearSessionTimeout();
          return;
        }

        if (this.getToken() && !this.timeoutId) {
          void this.initializeSession();
        }
      });
  }

  public login(loginData: any): Observable<any> {
    const loginUrl = `${this.config.get('COLLECT_API_BASE_URL')}${this.apiUrl.LOGIN}`;
    return this.http.post(loginUrl, loginData);
  }

  public verifyOtp(payload: { email: string; otp: number }): Observable<any> {
    const verifyOtpUrl = `${this.config.get('COLLECT_API_BASE_URL')}/auth/login-with-otp`;
    return this.http.post(verifyOtpUrl, payload);
  }

  public resendOtp(payload: { email: string }): Observable<any> {
    const resendOtpUrl = `${this.config.get('COLLECT_API_BASE_URL')}/auth/resend-otp`;
    return this.http.post(resendOtpUrl, payload);
  }

  public createPassword(payload: any): Observable<any> {
    const url = `${this.config.get('COLLECT_API_BASE_URL')}/users/registration/complete`;
    return this.http.post(url, payload);
  }

  public storeTokens(token: string, refreshToken: string): void {
    localStorage.setItem('token', token);
    localStorage.setItem('refreshToken', refreshToken);
  }

  public storeCurrentUser(userData: UserData): void {
    localStorage.setItem('currentUser', JSON.stringify(userData));
  }

  public getCurrentUser(): UserData | null {
    const dataString = localStorage.getItem('currentUser');
    return dataString ? JSON.parse(dataString) : null;
  }

  public getToken(): string {
    return localStorage.getItem('token') || '';
  }
  public getRefreshToken(): string {
    return localStorage.getItem('refreshToken') || '';
  }

  public isTokenExpired(offsetSeconds: number = 60): boolean {
    const token = this.getToken();
    return !token || this.jwtHelper.isTokenExpired(token, offsetSeconds);
  }

  public refreshToken(refreshToken: string): Observable<any> {
    const refreshTokenUrl = `${this.config.get('COLLECT_API_BASE_URL')}${this.apiUrl.REFRESH_TOKEN}`;
    return this.http.post(refreshTokenUrl, { refreshToken });
  }

  async handleTokenRefresh(): Promise<string> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      this.logout();
      throw new Error('No refresh token available. Please login again.');
    }

    if (!this.refreshInProgress$.value) {
      const refreshPromise = lastValueFrom(this.refreshToken(refreshToken))
        .then((response: any) => {
          this.storeTokens(response.token, response.refreshToken);
          this.refreshInProgress$.next(null);
          return response.token;
        })
        .catch((err) => {
          this.refreshInProgress$.next(null);
          this.logout();
          throw err;
        });

      this.refreshInProgress$.next(refreshPromise);
    }

    return this.refreshInProgress$.value!;
  }

  public async fetchSessionSettings(): Promise<void> {
    const orgKey = this.coreService.orgKey;
    if (!orgKey) return;

    try {
      const settings = await lastValueFrom(this.midaApiService.getOrganisationSettings(orgKey));
      this.sessionTimeoutValue = settings?.userActivitySettings?.sessionTimeout || 5;
    } catch (error) {
      console.error('Failed to fetch session timeout settings', error);
    }
  }

  public async initializeSession(): Promise<void> {
    if (!this.getToken() || this.isExcludedFromSessionTimeout()) {
      this.clearSessionTimeout();
      return;
    }

    this.resetSessionTimeout();
    await this.fetchSessionSettings();
    this.resetSessionTimeout();
  }

  onUserActivity(): void {
    if (!this.getToken() || this.isExcludedFromSessionTimeout()) {
      return;
    }

    this.resetSessionTimeout();
  }

  private startSessionTimeout(): void {
    if (!this.getToken() || this.isExcludedFromSessionTimeout()) {
      this.clearSessionTimeout();
      return;
    }

    this.clearSessionTimeout();
    this.timeoutId = setTimeout(() => {
      this.ngZone.run(() => {
        if (this.isExcludedFromSessionTimeout()) {
          this.clearSessionTimeout();
          return;
        }
        this.logout();
      });
    }, this.sessionTimeoutValue * 60 * 1000);
  }

  resetSessionTimeout(): void {
    this.startSessionTimeout();
  }

  public isExcludedFromSessionTimeout(): boolean {
    const routerUrl = this.router.url || '';
    const path = typeof window !== 'undefined' ? window.location.pathname : '';
    const url = routerUrl || path;

    return (
      url.includes('/loan-tokenisation') ||
      url.includes('/paygate/gateway') ||
      url.includes('/payment-details') ||
      path.includes('/loan-tokenisation') ||
      path.includes('/paygate/gateway') ||
      path.includes('/payment-details') ||
      url === '/login' ||
      path === '/login'
    );
  }

  clearSessionTimeout(): void {
    if (this.timeoutId !== null) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
  }

  public logout(): void {
    const currentUrl = this.router.url;
    const stayOnPublicPage = this.isExcludedFromSessionTimeout();

    this.clearSessionTimeout();
    localStorage.clear();
    this.navigationHistory.clear();

    // Public pages (loan tokenisation, payment details) should not be forced to login.
    if (stayOnPublicPage) {
      return;
    }

    localStorage.setItem('redirectUrl', currentUrl);
    this.router.navigate(['/login']);
  }

  public forgot(payload: { email: string }): Observable<any> {
    const forgotPasswordUrl = `${this.config.get('COLLECT_API_BASE_URL')}${this.apiUrl.FORGOT_PASSWORD}`;
    return this.http.post(forgotPasswordUrl, payload);
  }

  public resetPassword(resetData: any): Observable<any> {
    const resetPassUrl = `${this.config.get('COLLECT_API_BASE_URL')}${this.apiUrl.RESET_PASSWORD}`;
    return this.http.post(resetPassUrl, resetData);
  }
}
